import {
  attachPersistentDisk,
  selectActiveMountPointsByServiceId,
  selectDiskAttachmentsByPersistentDiskId,
  selectServicesByEnvId,
} from "@app/deploy";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useSelector,
} from "@app/react";
import { environmentActivityUrl } from "@app/routes";
import type { WebState } from "@app/schema";
import type { DeployPersistentDisk } from "@app/types";
import { useValidator } from "@app/ui/hooks";
import { Banner, BannerMessages } from "@app/ui/shared/banner";
import { Button } from "@app/ui/shared/button";
import { Form, FormGroup } from "@app/ui/shared/form-group";
import { IconCylinder } from "@app/ui/shared/icons";
import { Input } from "@app/ui/shared/input";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeading,
} from "@app/ui/shared/modal";
import { ModalWrapper } from "@app/ui/shared/modal-portal";
import { Select, type SelectOption } from "@app/ui/shared/select";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DiskRestartWarning } from "./disk-restart-warning";

interface AttachFormData {
  serviceId: string;
  mountPoint: string;
  // Mount points already in use on the chosen service (active attachments).
  // Lower-cased & trimmed for case-insensitive comparison.
  existingMountPoints: string[];
}

// A mount point such as `/mnt/foo`. Normalize leading/trailing whitespace
// before validating & submitting.
export const normalizeMountPoint = (raw: string) => raw.trim();

const EMPTY_MOUNT_POINTS: string[] = [];

const attachValidators = {
  serviceId: (data: AttachFormData) => {
    if (!data.serviceId) return "Select a service to attach this disk to";
  },
  mountPoint: (data: AttachFormData) => {
    const mountPoint = normalizeMountPoint(data.mountPoint);
    if (!mountPoint) return "Mount point is required";
    if (!mountPoint.startsWith("/")) {
      return "Mount point must be an absolute path (e.g., /mnt/data)";
    }
    if (data.existingMountPoints.includes(mountPoint.toLowerCase())) {
      return "That mount point is already in use on this service";
    }
  },
};

interface AttachPersistentDiskModalProps {
  isOpen: boolean;
  disk: DeployPersistentDisk;
  onClose: () => void;
}

export const AttachPersistentDiskModal = ({
  isOpen,
  disk,
  onClose,
}: AttachPersistentDiskModalProps) => {
  const dispatch = useDispatch();
  const [serviceId, setServiceId] = useState("");
  const [mountPoint, setMountPoint] = useState("");

  // Scope the service picker to the disk's own environment, then restrict
  // to app services only — persistent disks cannot attach to database services.
  const allEnvServices = useSelector((s) =>
    selectServicesByEnvId(s, { envId: disk.environmentId }),
  );
  const services = useMemo(
    () => allEnvServices.filter((s) => s.appId !== ""),
    [allEnvServices],
  );

  // A disk has at most one active attachment; paths change via detach ->
  // re-attach. As defense in depth, detect any active (non-deprovisioned)
  // attachment so we can block this flow entirely — an already-attached disk
  // cannot be attached again.
  const diskAttachments = useSelector((s: WebState) =>
    selectDiskAttachmentsByPersistentDiskId(s, { persistentDiskId: disk.id }),
  );
  const hasActiveAttachment = useMemo(
    () => diskAttachments.some((a) => a.status !== "deprovisioned"),
    [diskAttachments],
  );

  // The container-count guard reads off the selected service entity.
  const selectedService = useMemo(
    () => services.find((s) => s.id === serviceId),
    [services, serviceId],
  );

  // Active attachments on the chosen service back the mount-point uniqueness
  // check. Detached (deprovisioned) attachments free up their mount point.
  const existingMountPoints = useSelector((s: WebState) =>
    serviceId
      ? selectActiveMountPointsByServiceId(s, { serviceId })
      : EMPTY_MOUNT_POINTS,
  );

  const [errors, validate] = useValidator<
    AttachFormData,
    typeof attachValidators
  >(attachValidators);

  const navigate = useNavigate();
  const loader = useLoader(attachPersistentDisk);

  const handleClose = () => {
    setServiceId("");
    setMountPoint("");
    onClose();
  };

  useLoaderSuccess(loader, () => {
    // TODO: use appActivityUrl once operations properly populate targetResources
    // with the service and app being attached to.
    navigate(environmentActivityUrl(disk.environmentId));
  });

  // A disk can only attach to a service running exactly one container.
  // This covers both the 0-container ("not running") and 2+ ("scaled out")
  // cases — block submit and surface a warning when a service is selected.
  const containerCount = selectedService?.containerCount ?? 0;
  const containerCountInvalid =
    Boolean(selectedService) && containerCount !== 1;
  const containerCountWarning = selectedService
    ? containerCount === 0
      ? `${selectedService.handle} is not running (0 containers). Scale it to exactly 1 container before attaching a disk.`
      : containerCount > 1
        ? `${selectedService.handle} is running ${containerCount} containers. A persistent disk can only attach to a service scaled to exactly 1 container.`
        : ""
    : "";

  const serviceOptions: SelectOption[] = [
    { label: "Select a service", value: "" },
    ...services.map((s) => ({
      label: `${s.handle} (${s.containerCount} container${
        s.containerCount === 1 ? "" : "s"
      })`,
      value: s.id,
    })),
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data: AttachFormData = { serviceId, mountPoint, existingMountPoints };
    if (!validate(data)) return;
    // Backstop: never dispatch when the container-count guard is tripped, or
    // when the disk already has an active attachment (a disk attaches to one
    // service at a time).
    if (containerCountInvalid || hasActiveAttachment) return;
    dispatch(
      attachPersistentDisk({
        id: disk.id,
        serviceId,
        mountPoint: normalizeMountPoint(mountPoint),
      }),
    );
  };

  if (!isOpen) return null;

  const submitDisabled =
    !serviceId ||
    !mountPoint.trim() ||
    containerCountInvalid ||
    hasActiveAttachment;

  return (
    <ModalWrapper>
      <Modal isOpen={isOpen} onClose={handleClose} className="min-w-[600px]">
        <ModalHeading
          icon={<IconCylinder variant="sm" />}
          title={`Attach Disk: ${disk.handle}`}
          onClose={handleClose}
        />
        <Form onSubmit={handleSubmit}>
          <ModalBody className="pb-0">
            <DiskRestartWarning className="mb-4" />

            {hasActiveAttachment ? (
              <Banner variant="error" className="mb-4">
                This disk is already attached. Detach it first — a disk attaches
                to one service at a time.
              </Banner>
            ) : null}

            <FormGroup
              label={
                <>
                  Service <span className="text-red-500">*</span>
                </>
              }
              htmlFor="attach-disk-service"
              className="mb-4"
              feedbackMessage={errors.serviceId}
              feedbackVariant={errors.serviceId ? "danger" : "default"}
            >
              <Select
                id="attach-disk-service"
                ariaLabel="service"
                options={serviceOptions}
                value={serviceId}
                onSelect={(opt) => setServiceId(opt.value)}
              />
            </FormGroup>

            {containerCountWarning ? (
              <Banner variant="error" className="mb-4">
                {containerCountWarning}
              </Banner>
            ) : null}

            <FormGroup
              label={
                <>
                  Mount Point <span className="text-red-500">*</span>
                </>
              }
              htmlFor="attach-disk-mount-point"
              className="mb-4"
              feedbackMessage={errors.mountPoint}
              feedbackVariant={errors.mountPoint ? "danger" : "default"}
            >
              <Input
                id="attach-disk-mount-point"
                name="mountPoint"
                value={mountPoint}
                onChange={(e) => setMountPoint(e.target.value)}
                placeholder="e.g., /mnt/data"
                maxLength={255}
              />
            </FormGroup>

            <BannerMessages className="mt-4" {...loader} />
          </ModalBody>
          <ModalFooter
            actions={[
              <Button
                key="submit"
                type="submit"
                disabled={submitDisabled}
                isLoading={loader.isLoading}
              >
                Attach Disk
              </Button>,
            ]}
          />
        </Form>
      </Modal>
    </ModalWrapper>
  );
};
